import type { ChangeEvent, ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { ReclamationAccessPolicy } from 'graphql-ts-types';
import { color, display, fontSize, fontWeight, lineHeight, space } from 'styled-system';

import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import { Text } from 'common/components/Text';
import type { TextAreaComponent } from 'common/components/Textarea';
import Textarea from 'common/components/Textarea';
import { useLocalization } from 'common/providers/LocaleProvider';

interface AdditionalFeedbackProps {
  value: string;
  isCommentDisabled?: boolean;
  shouldValidate?: boolean;
  commentPolicy: ReclamationAccessPolicy;
  onChange: (comment: string) => void;
}

const UnstyledLabel = styled.label<any>`
  ${color};
  ${display};
  ${fontSize};
  ${fontWeight};
  ${lineHeight};
  ${space};
`;

const Label = (props: ComponentProps<typeof UnstyledLabel>) => (
  <UnstyledLabel
    color="sys.neutral.text.strong"
    display="block"
    fontSize="fs_16"
    fontWeight="bold"
    lineHeight="lh_20"
    mb="sp_4"
    {...props}
  />
);

const UnstyledDescription = styled(Text)``;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription
    fontSize="fs_16"
    color="sys.neutral.text.default"
    lineHeight="lh_20"
    {...props}
  />
);

const UnstyledStyledTextarea: TextAreaComponent = styled(Textarea)``;

const StyledTextarea = (props: ComponentProps<typeof UnstyledStyledTextarea>) => (
  <UnstyledStyledTextarea mt="sp_16" mb="sp_16" {...props} />
);

const AdditionalFeedback = ({
  value: comment,
  isCommentDisabled,
  commentPolicy,
  shouldValidate,
  onChange,
}: AdditionalFeedbackProps) => {
  const { t } = useLocalization();

  const handleCommentChange = ({ target }: ChangeEvent<HTMLInputElement>) => onChange(target.value);

  return (
    <>
      <Label data-testid="additional-information-label" htmlFor="additional-information-comment">
        {t('h24_additional_feedback')}
        {commentPolicy !== ReclamationAccessPolicy.Required ? ` (${t('h24_optional')})` : ''}
      </Label>
      {shouldValidate && comment === '' && (
        <ReclamationAlert message={t('h24_additional_feedback_error')} />
      )}
      <Description>{t('h24_comment_input_label_description')}</Description>
      {commentPolicy === ReclamationAccessPolicy.Required ? (
        <Description mt="sp_4">{t('h24_additional_feedback_length_message')}</Description>
      ) : null}
      <StyledTextarea
        data-testid="additional-information-comment"
        id="additional-information-comment"
        value={comment}
        placeholder={t('h24_write_comment_here')}
        disabled={isCommentDisabled}
        onChange={handleCommentChange}
      />
    </>
  );
};

export default AdditionalFeedback;
